import { Link } from "react-router-dom";

import LegalPage from "../LegalPage";
import { PIGGY_EMAIL } from "../constants";

const Mail = ({ subject }) => (
  <a href={`mailto:${PIGGY_EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`}>
    {PIGGY_EMAIL}
  </a>
);

const SECTIONS = [
  {
    id: "agreement",
    title: "The agreement",
    body: (
      <>
        <p>
          These terms are a contract between you and the developer and operator
          of Piggy, based in India ("we", "us"), for your use of the Piggy
          Android app, web dashboard, and API (the "Service"). By
          signing in to Piggy you accept them. If you do not agree, do not use
          the Service.
        </p>
        <p>
          Our <Link to="/piggy/privacy">Privacy policy</Link> explains how we
          handle your data and is part of these terms.
        </p>
      </>
    ),
  },
  {
    id: "what",
    title: "What Piggy is, and is not",
    body: (
      <>
        <p>
          Piggy reads transaction alerts from your SMS inbox and, optionally,
          your Gmail, and uses AI to organise them into a personal ledger. It
          is a record-keeping tool.
        </p>
        <p>Piggy is <strong>not</strong>:</p>
        <ul>
          <li>
            a bank, wallet, payment service, or account aggregator. It cannot
            move money or access your bank accounts. It only reads the alerts
            your bank chooses to send you;
          </li>
          <li>
            a source of financial, tax, investment, or legal advice. Nothing in
            the app is a recommendation;
          </li>
          <li>
            an authoritative statement of your finances. Your bank's records
            are. Piggy can only be as complete as the alerts it receives, and
            AI extraction can make mistakes.
          </li>
        </ul>
        <p>
          Always check important figures against your bank before relying on
          them.
        </p>
      </>
    ),
  },
  {
    id: "early-access",
    title: "Early access",
    body: (
      <p>
        Piggy is currently in early access. Features may change, break, or be
        removed; data formats may change; and the Service may be paused or
        discontinued. We will do our best to warn you before anything that
        affects your data, and to let you export it first. The Service is free
        during early access. If we ever introduce paid features, we will tell
        you clearly beforehand and nothing you already have will be put behind
        a paywall without notice.
      </p>
    ),
  },
  {
    id: "eligibility",
    title: "Who may use Piggy",
    body: (
      <p>
        You must be at least 18 years old, able to enter a binding contract,
        and using Piggy for your own personal, non-commercial finances. You
        must sign in with a Google account that belongs to you, and you may
        only connect an SMS inbox or Gmail mailbox that you own or have clear
        authority to connect.
      </p>
    ),
  },
  {
    id: "account",
    title: "Your account",
    body: (
      <>
        <p>
          Your Google account is your Piggy account. You are responsible for
          keeping that account, your device, and your device lock secure. If
          you believe someone else has accessed your Piggy ledger, tell us at{" "}
          <Mail subject="Piggy account security" /> and revoke Piggy's access
          from your Google Account.
        </p>
        <p>
          You may stop using Piggy at any time by uninstalling the app and
          signing out of the dashboard. To delete your account and data, follow
          the steps in the <Link to="/piggy/privacy#control">Privacy policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: "permissions",
    title: "Permissions you grant",
    body: (
      <>
        <p>
          Piggy works only with what you allow. Granting the SMS permission lets
          the app read incoming messages on your device to find transaction
          alerts. Connecting Gmail grants Piggy read-only access to your mailbox
          through Google. You can withdraw either at any time in Android
          settings, in the app's Settings, or at your Google Account's
          permissions page, and Piggy will stop reading from that source.
        </p>
        <p>
          Your use of Gmail through Piggy is also subject to Google's terms and
          Google's{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            API Services User Data Policy
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>
            connect another person's SMS inbox or mailbox without their clear
            permission, or use Piggy to monitor anyone else;
          </li>
          <li>
            attempt to access other users' data, probe or overload the API,
            bypass rate limits, or interfere with the Service;
          </li>
          <li>
            reverse-engineer, resell, or offer the Service to third parties, or
            use it to build a competing product's dataset;
          </li>
          <li>
            upload content that is unlawful or that you have no right to
            upload, including statements that are not yours;
          </li>
          <li>use Piggy in violation of any law that applies to you.</li>
        </ul>
        <p>
          We may suspend or close accounts that break these rules, after
          warning you where that is practical.
        </p>
      </>
    ),
  },
  {
    id: "your-content",
    title: "Your content",
    body: (
      <p>
        Your alerts, transactions, edits, categories, rules, and imported
        statements are yours. You give us a limited licence to store and
        process them solely to provide the Service to you, as described in the
        Privacy policy. That licence ends when the data is deleted. We claim no
        other rights over your content and will not use it to train AI models
        or build products for others.
      </p>
    ),
  },
  {
    id: "our-ip",
    title: "Our rights",
    body: (
      <p>
        The Piggy name, icon, app, dashboard, design, and code are ours or our
        licensors'. We grant you a personal, revocable, non-transferable licence
        to install and use the app and dashboard for your own use under these
        terms. Bank, card-network, and merchant names shown in your ledger
        belong to their respective owners; Piggy is not affiliated with or
        endorsed by any of them, or by Google.
      </p>
    ),
  },
  {
    id: "third-parties",
    title: "Third-party services",
    body: (
      <p>
        Piggy depends on Google (sign-in and Gmail), Amazon Web Services
        (hosting), and an AI model provider (extraction). Their availability and
        terms are outside our control. If one of them changes or withdraws a
        service, some Piggy features may stop working and we may need to change
        how Piggy operates.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "No warranty",
    body: (
      <p>
        The Service is provided <strong>"as is" and "as available"</strong>,
        without warranties of any kind, express or implied, including accuracy,
        completeness, fitness for a particular purpose, and uninterrupted
        operation. Extracted amounts, payees, dates, and categories may be
        wrong or missing. Alerts your bank does not send, or that arrive in an
        unrecognised format, will not appear. You use Piggy at your own risk
        and remain responsible for your own financial decisions.
      </p>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the fullest extent permitted by law, we are not liable for any
          indirect, incidental, consequential, special, or punitive loss, or for
          any loss of profit, data, or goodwill, arising from your use of or
          inability to use the Service, including decisions you make based on
          figures shown in Piggy. Our total liability for all claims relating to
          the Service is limited to the greater of ₹1,000 or the amount you paid
          us for the Service in the twelve months before the claim (currently
          nil).
        </p>
        <p>
          Nothing in these terms limits liability that cannot be limited under
          applicable law, including for fraud or for death or personal injury
          caused by negligence.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Your responsibility to us",
    body: (
      <p>
        You agree to compensate us for losses and reasonable costs that arise
        from your breach of these terms or your unlawful use of the Service,
        including connecting an inbox or mailbox you had no right to connect.
      </p>
    ),
  },
  {
    id: "termination",
    title: "Ending the relationship",
    body: (
      <p>
        You may stop at any time. We may suspend or end your access if you
        break these terms, if required by law, or if we discontinue the Service;
        in the last case we will give reasonable notice and a chance to export
        your data. Sections on your content, our rights, no warranty, limitation
        of liability, and governing law survive termination.
      </p>
    ),
  },
  {
    id: "law",
    title: "Governing law and disputes",
    body: (
      <p>
        These terms are governed by the laws of India. Before starting any
        formal proceeding, you agree to write to us at{" "}
        <Mail subject="Piggy dispute" /> and give us 30 days to resolve the
        matter informally. Subject to that, the courts of India have exclusive
        jurisdiction. If you are a consumer in a country whose law gives you
        rights that cannot be waived by contract, those rights are unaffected.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to these terms",
    body: (
      <p>
        We may update these terms as Piggy evolves. For material changes we
        will notify you in the app at least 14 days before they take effect,
        unless a change is needed sooner for legal or security reasons.
        Continuing to use Piggy after the effective date means you accept the
        new terms. If you do not, stop using the Service and ask us to delete
        your account.
      </p>
    ),
  },
  {
    id: "general",
    title: "General",
    body: (
      <p>
        These terms, with the Privacy policy, are the entire agreement between
        us about the Service. If any part is found unenforceable, the rest
        stands. Our not enforcing a term is not a waiver. You may not assign
        these terms; we may assign them to a successor who takes over Piggy,
        with notice to you.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Piggy · <Mail subject="Piggy terms" />.
      </p>
    ),
  },
];

const Terms = () => (
  <LegalPage
    kind="terms"
    title="Terms of service"
    description="The terms for using Piggy, an independent app that turns bank alerts into a personal ledger."
    intro={
      <>
        <p>
          Piggy is a small, independent app. These terms are written to be
          read, not skimmed: they say what Piggy is, what it is not, what you
          can expect from us, and what we ask of you.
        </p>
        <p>
          <strong>In one line:</strong> Piggy is a record-keeping tool built
          from your bank's alerts, offered as-is during early access; check
          important numbers with your bank, use only inboxes you own, and we
          will treat your data as described in the Privacy policy.
        </p>
      </>
    }
    sections={SECTIONS}
  />
);

export default Terms;
